import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from '@tanstack/react-router'
import { mdiFileChartOutline } from '@mdi/js'
import { apiClient } from '../../api/client'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { useAsset } from '../../hooks/useQueries'
import { PageHeader } from '../shared/PageHeader'
import { EmptyState } from '../ui/empty-state'
import { ApiSearchableSelect, type ApiSearchableSelectOption } from '../ApiSearchableSelect'
import { EngagementScore } from './EngagementScore'

interface Props {
    org: string
    // Selected building/site — the report scope. Lives in the URL so a
    // generated report is shareable (routing-state convention).
    assetUid?: string
    // Active readiness lens (sale / bank / insurance).
    lens?: string
}

// Only sites and buildings carry a dossier: the completeness score, the
// expected-document catalog and every legal output below are building-level.
// Mirrors the scope the engagement tab used to apply on the asset page.
const REPORT_SCOPE_ASSET_TYPES = 'building_group,building'

/**
 * Organization-level Reports section (spec 2026-08-26-01).
 *
 * A report is an object, not an action: `report_runs` carry a stable UID, a
 * frozen data snapshot and an archive flag, so they belong to a collection page
 * of their own rather than to a fifteenth tab on the asset. This page is the
 * antechamber — the completeness score reads as "what separates this building
 * from a publishable document", which is exactly what it measures. The catalog
 * of the ten outputs and the generation history land here in the next lots.
 */
export function ReportsPage({ org, assetUid, lens }: Props) {
    const { t } = useTranslation('reports')
    const navigate = useNavigate()

    const breadcrumbs = useMemo(
        () => [
            { title: t('common:nav.home'), path: `/${org}` },
            { title: t('nav_title') },
        ],
        [org, t],
    )
    useBreadcrumbs(breadcrumbs)

    // Asset picker restricted to the report scope. Empty query lists the most
    // recently touched sites/buildings so the select is useful before typing.
    const searchAssets = useCallback(async (query: string): Promise<ApiSearchableSelectOption[]> => {
        const { data, error } = await apiClient.GET('/api/v1/organizations/{orgCode}/assets', {
            params: {
                path: { orgCode: org },
                query: {
                    limit: 50,
                    asset_type_code: REPORT_SCOPE_ASSET_TYPES,
                    ...(query ? { q: query, sort: 'name' } : { sort: '-updated_at' }),
                } as Record<string, unknown>,
            },
        })
        if (error) return []
        const assets = ((data as { data?: Array<Record<string, unknown>> })?.data) ?? []
        return assets.map((asset) => ({
            value: String(asset.uid),
            label: String(asset.name || asset.uid),
        }))
    }, [org])

    // The scope can arrive from a deep link (the asset header button), in which
    // case the select has never seen the asset and would show its UID over a
    // real selection. Reuse the shared asset hook so the name resolves off the
    // cache the user most likely arrives with.
    const { data: selectedAsset } = useAsset(org, assetUid || '')

    const initialOptions = useMemo<ApiSearchableSelectOption[]>(() => (
        assetUid
            ? [{ value: assetUid, label: String(selectedAsset?.name || assetUid) }]
            : []
    ), [assetUid, selectedAsset])

    const selectAsset = useCallback((uid: string) => {
        navigate({ to: '/$org/reports', params: { org }, search: { asset: uid || undefined, lens } })
    }, [navigate, org, lens])

    const changeLens = useCallback((next: string) => {
        navigate({ to: '/$org/reports', params: { org }, search: { asset: assetUid, lens: next } })
    }, [navigate, org, assetUid])

    return (
        <div className="container mx-auto py-6 space-y-6" data-testid="reports-page">
            <PageHeader
                icon={mdiFileChartOutline}
                title={t('nav_title')}
                description={t('subtitle')}
            />

            <div className="max-w-md">
                <label className="text-sm text-muted-foreground" htmlFor="reports-asset-select">
                    {t('scope.label')}
                </label>
                <div className="mt-1">
                    <ApiSearchableSelect
                        // ApiSearchableSelect snapshots `initialOptions` into
                        // state at mount, so a label that resolves later never
                        // reaches the trigger. Remount when it does — the cost
                        // is nil on a closed dropdown.
                        key={initialOptions[0]?.label ?? 'empty'}
                        value={assetUid}
                        placeholder={t('scope.placeholder')}
                        searchPlaceholder={t('scope.search_placeholder')}
                        emptyMessage={t('scope.empty')}
                        onSearch={searchAssets}
                        onValueChange={selectAsset}
                        // 0 so opening the select lists the recent buildings
                        // straight away — picking a scope should not require
                        // guessing a name first.
                        minSearchLength={0}
                        initialOptions={initialOptions}
                        data-testid="reports-asset-select"
                    />
                </div>
            </div>

            {assetUid ? (
                <EngagementScore
                    org={org}
                    assetUid={assetUid}
                    lens={lens}
                    onLensChange={changeLens}
                />
            ) : (
                <EmptyState
                    icon={mdiFileChartOutline}
                    title={t('empty.title')}
                    description={t('empty.body')}
                    data-testid="reports-empty"
                />
            )}
        </div>
    )
}
